import { useState } from "react";
import Pause from "./Icons/Pause.svg";
import Play from "./Icons/Play.svg";
import Prev from "./Icons/Prev.svg";
import Next from "./Icons/Next.svg";
import Forward from "./Icons/Forward.svg";
import Backward from "./Icons/Backward.svg";
// import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Logo from "../../logo-no-text.png";
import { publish, subscribe, unsubscribe } from "../../PlayerPubSub";
import "./index.scss";
import mixpanel from "mixpanel-browser";
import userStore from "../../Stores/User";
import React, { useEffect, useMemo, useRef } from "react";
// import LiveHelpIcon from "@material-ui/icons/LiveHelp";
// import SettingsIcon from "@material-ui/icons/Settings";
// import FullscreenIcon from "@material-ui/icons/Fullscreen";
// import FullscreenExitIcon from "@material-ui/icons/FullscreenExit";
// import CancelIcon from "@material-ui/icons/Cancel";
// import useMediaQuery from "@material-ui/core/useMediaQuery";

import { 
  Settings as SettingsIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { useMediaQuery } from "@mui/material";

import { observer } from "mobx-react";

const formatTime = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds - minutes * 60);

  return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
};

const getHostName = (link) => {
  if (link) {
    const doc = document.createElement("a");
    doc.href = (link.startsWith("http") ? "" : "http://") + link;
    return doc.hostname;
  }
};

const ProgressBar = ({ timestamp, duration, onSeek }) => {
  const percent = duration && duration > 0 ? timestamp / duration : 0;
  
  const handleSeek = (e) => {
    const bounds = e.target.parentElement.getBoundingClientRect();
    const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
    const newPercent =
      bounds.width && bounds.width > 0
        ? (clientX - bounds.left) / bounds.width
        : 0;

    onSeek(newPercent * duration);
  };

  const onMouseDown = (e) => {
    handleSeek(e);
  };

  const onTouchStart = (e) => {
    e.stopPropagation();
    handleSeek(e);
  };

  return (
    <div className="progress-bar">
      <div className="unplayed" />
      <div className="played" style={{ width: `${percent * 100}%` }} />
      <div className="click-layer" onMouseDown={onMouseDown} onTouchStart={onTouchStart} />
    </div>
  );
};

const Player = observer(
  ({
    listeningWhere,
    currentArticle,
    onPreviousArticle,
    onNextArticle,
    onArticleEnded,
    onDisablePlayer,
    onLoadedMetadata,
    isFullPlayer,
    updateIsFullPlayer,
    autoPlay,
    fullPlayerLeaveSpace,
  }) => {
    const isDesktop = useMediaQuery("(min-width: 1200px)");
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [duration, setDuration] = useState(0);
    const [timestamp, setTimestamp] = useState(0);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);

    const { idToken, isSignedIn, trackingId } = userStore;
    const renderPlayButton = () => {
      // Renders a link to payment, a play button, or a loading icon
      // Depending on the article, user login status, etc.
      return (
        <div>
          <img
            style={{ width: 22, height: 26 }}
            src={isPlaying ? Pause : Play}
            alt={isPlaying ? "Pause" : "Play"}
            onClick={isPlaying ? forcePause : forcePlay}
            onTouchEnd={(e) => {
              e.stopPropagation();
              // Add a small delay to prevent rapid state changes
              setTimeout(() => {
                if (isPlaying) {
                  forcePause();
                } else {
                  forcePlay();
                }
              }, 50);
            }}
          />
        </div>
      );
    };

    const setPlaying = useMemo(
      () => () => {
        setIsPlaying(true);
      },
      []
    );

    const setPaused = useMemo(
      () => () => {
        setIsPlaying(false);
      },
      []
    );

    const forcePlay = useMemo(
      () => async () => {
        if (audioRef.current) {
          try {
            // Check if audio is ready to play
            if (audioRef.current.readyState < 2) {
              // Audio not ready, wait for it to load
              audioRef.current.load();
              await new Promise((resolve) => {
                const handleCanPlay = () => {
                  audioRef.current.removeEventListener('canplay', handleCanPlay);
                  resolve();
                };
                audioRef.current.addEventListener('canplay', handleCanPlay);
              });
            }
            
            await audioRef.current.play();
            setPlaying();
          } catch (error) {
            console.error("Play error:", error);
            // If play was interrupted by pause, try again after a short delay
            if (error.name === "AbortError") {
              setTimeout(async () => {
                try {
                  await audioRef.current.play();
                  setPlaying();
                } catch (retryError) {
                  console.error("Retry play error:", retryError);
                  setPaused();
                }
              }, 100);
            } else {
              setPaused();
            }
          }
        }
      },
      [setPlaying, setPaused]
    );

    const forcePause = useMemo(
      () => () => {
        console.log("forcePause called");
        if (audioRef.current) {
          // For iOS, we need to be more explicit about pausing
          audioRef.current.pause();
          
          // Ensure the pause state is properly set
          setTimeout(() => {
            if (audioRef.current && !audioRef.current.paused) {
              console.log("Audio still playing, forcing pause again");
              audioRef.current.pause();
            }
          }, 10);
        }

        setPaused();
      },
      [setPaused]
    );

    const getArticleUrl = () => {
      if (!currentArticle) {
        return null;
      }
      // NOTE: I don't think we need article_url anymore?
      if (currentArticle.article_url) {
        return currentArticle.article_url;
      }
      if (currentArticle.article_id) {
        return currentArticle.article_id;
      }

      return null;
    };

    const onEnded = () => {
      setPaused();

      if (onArticleEnded) {
        onArticleEnded();
      }

      if (onNextArticle) {
        onNextArticle();
      }
    };

    const onMetaDataChange = () => {
      if (audioRef.current) {
        setDuration(audioRef.current.duration);
      }

      if (onLoadedMetadata) {
        onLoadedMetadata();
      }
    };

    const onSeek = (newCurrentTime) => {
      newCurrentTime = Math.min(Math.max(newCurrentTime, 0), duration);

      if (audioRef.current) {
        audioRef.current.currentTime = newCurrentTime;
      }

      setTimestamp(newCurrentTime);
    };

    const seekForward = () => {
      onSeek(audioRef.current ? audioRef.current.currentTime + 30 : 0);
    };

    const seekBackward = () => {
      onSeek(audioRef.current ? audioRef.current.currentTime - 30 : 0);
    };

    const updatePlaybackSpeed = ({ target: { value } }) => {
      const speed = parseInt(value, 10) / 10000;

      if (audioRef.current) {
        audioRef.current.playbackRate = speed;
      }

      setPlaybackSpeed(speed);
    };

    const makeFullPlayer = () => {
      if (updateIsFullPlayer) {
        updateIsFullPlayer(true);
      }
    };

    const closeFullPlayer = () => {
      if (updateIsFullPlayer) {
        updateIsFullPlayer(false);
      }
    };

    useEffect(() => {
      const intervalTime = 200;

      const intervalId = window.setInterval(() => {
        if (audioRef.current) {
          setTimestamp(audioRef.current.currentTime);
          // Only update playing state if it's different to prevent iOS override
          const currentlyPaused = audioRef.current.paused;
          if (isPlaying !== !currentlyPaused) {
            setIsPlaying(!currentlyPaused);
          }
        }
      }, intervalTime);

      return () => {
        window.clearInterval(intervalId);
      };
    }, [isPlaying]);

    useEffect(() => {
      if (audioRef.current) {
        audioRef.current.playbackRate = playbackSpeed;
      }
    }, [currentArticle, playbackSpeed]);

    useEffect(() => {
      publish(isPlaying ? "playing" : "paused");
    }, [isPlaying]);

    useEffect(() => {
      subscribe("play", forcePlay);
      subscribe("pause", forcePause);

      return () => {
        unsubscribe("play", forcePlay);
        unsubscribe("pause", forcePause);
      };
    }, [forcePlay, forcePause]);

    const playbackStepMin = 5000;
    const playbackStepMax = 30000;
    const playbackStep = 1000;

    return (
      <>
        <audio
          key={currentArticle ? currentArticle.audio_url : "player"}
          autoPlay={false}
          controls={false}
          ref={audioRef}
          playsInline={true}
          webkit-playsinline="true"
          preload="metadata"
          onPlay={(e) => {
            console.log("playing")
            setPlaying();
            if (getArticleUrl()) {
              mixpanel.track("Play Article", {
                ...currentArticle,
                article_url: getArticleUrl(),
              });
            }
          }}
          onError={(e) => {
            // Handle audio loading errors gracefully
            console.error("Audio error:", e);
            setPaused();
            
            if (currentArticle) {
              console.log("Error loading audio for:", currentArticle.title);
              
              // Check if this is the 403 error from the S3 URL
              if (currentArticle.audio_url && 
                  currentArticle.audio_url.includes('s3-us-west-2.amazonaws.com')) {
                console.log("Detected S3 403 error - won't attempt retry");
                // Don't attempt to auto-retry, this will cause the refresh loop
              }
            }
            
            // Prevent the browser from attempting to reload or refresh
            e.preventDefault();
            e.stopPropagation();
          }}
          onLoadStart={() => {
            // Audio is starting to load
            console.log("Audio loading started");
          }}
          onCanPlay={() => {
            // Audio is ready to play
            console.log("Audio ready to play");
          }}
          onPause={(e) => {
            console.log("Audio paused event triggered");
            // Only set paused if it's not a loading/error state
            if (audioRef.current && !audioRef.current.error) {
              setPaused();
            }
          }}
          onEnded={(e) => {
            onEnded();
            // Only track .article_url (/embed only)
            if (getArticleUrl()) {
              mixpanel.track("Finish Playing Article", {
                ...currentArticle,
                article_url: getArticleUrl(),
              });
            }
          }}
          onLoadedMetadata={onMetaDataChange}
        >
          {currentArticle ? <source src={currentArticle.audio_url} /> : null}
        </audio>

        {!isDesktop ? (
          isFullPlayer ? (
            <div
              className="player-full-mobile player-common"
              style={{ height: `calc(100vh - ${fullPlayerLeaveSpace}px)` }}
            >
              <div
                className="close-full-player-button"
                onClick={closeFullPlayer}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  closeFullPlayer();
                }}
              >
                <div>Close</div>
                <ExpandMoreIcon width="50" />
              </div>
              <div className="full-thumbnail">
                {currentArticle ? (
                  <img src={currentArticle.thumbnail_url || Logo} />
                ) : null}
              </div>
              <div className="title">
                {currentArticle ? currentArticle.title : ""}
              </div>

              <div className="author">
                {currentArticle ? (
                  <a
                    href={`http://${
                      currentArticle.article_id || currentArticle.article_url
                    }`}
                    target="_blank"
                    className="link"
                  >
                    {getHostName(
                      currentArticle.article_id || currentArticle.article_url
                    )}
                  </a>
                ) : null}
              </div>

              <div className="progress-and-buttons">
                <div className="buttons-other">
                  <a
                    href={userStore.customerPortalUrl}
                    target="_blank"
                    style={{
                      paddingLeft: "10px",
                      display:
                        listeningWhere === "/embed" && isSignedIn
                          ? "display"
                          : "none",
                    }}
                  >
                    <SettingsIcon />
                  </a>

                  <button
                    className="transparent-icon"
                    onClick={closeFullPlayer}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                      closeFullPlayer();
                    }}
                  >
                    <FullscreenExitIcon
                      style={{ height: "35px", width: "auto" }}
                    />
                  </button>
                </div>
                <div className="timestamp-progress">
                  <div className="timestamp">{formatTime(timestamp)}</div>
                  <ProgressBar {...{ timestamp, duration, onSeek }} />
                  <div className="duration">{formatTime(duration)}</div>{" "}
                </div>
                <div className="buttons-outer">
                  <div className="buttons">
                    <div>
                      <img
                        src={Prev}
                        alt="Previous article"
                        onClick={onPreviousArticle}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          onPreviousArticle();
                        }}
                      />
                    </div>
                    <div>
                      <img
                        src={Backward}
                        alt="Rewind 30 seconds"
                        onClick={seekBackward}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          seekBackward();
                        }}
                      />
                    </div>
                    {renderPlayButton()}

                    <div>
                      <img
                        src={Forward}
                        alt="Go forward 30 seconds"
                        onClick={seekForward}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          seekForward();
                        }}
                      />
                    </div>
                    <div>
                      <img
                        src={Next}
                        alt="Next article"
                        onClick={onNextArticle}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          onNextArticle();
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="speed">
                  <div className="title">Speed: {playbackSpeed}x</div>
                  <input
                    type="range"
                    onChange={updatePlaybackSpeed}
                    min={playbackStepMin}
                    max={playbackStepMax}
                    step={playbackStep}
                    value={playbackSpeed * 10000}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="player-mobile player-common">
              {onDisablePlayer ? (
                <div className="close-button" onClick={onDisablePlayer}>
                  <CancelIcon /> Remove Player
                </div>
              ) : null}
              <div 
                className="meta" 
                onClick={makeFullPlayer}
                onTouchStart={(e) => {
                  e.stopPropagation();
                  makeFullPlayer();
                }}
              >
                <div className="title">
                  {currentArticle ? currentArticle.title : ""}
                </div>
                <div
                  className="author"
                  style={{ display: listeningWhere === "/embed" && "none" }}
                >
                  {currentArticle ? (
                    <div className="link">
                      {getHostName(
                        currentArticle.article_id || currentArticle.article_url
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="buttons">
                <div className="back">
                  <img
                    src={Backward}
                    alt="Rewind 30 seconds"
                    onClick={seekBackward}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                      seekBackward();
                    }}
                  />
                </div>
                {renderPlayButton()}
                <div className="forward">
                  <img
                    src={Forward}
                    alt="Go forward 30 seconds"
                    onClick={seekForward}
                    onTouchStart={(e) => {
                      e.stopPropagation();
                      seekForward();
                    }}
                  />
                </div>
                <div 
                  className="full-player" 
                  onClick={makeFullPlayer}
                  onTouchStart={(e) => {
                    e.stopPropagation();
                    makeFullPlayer();
                  }}
                >
                  <FullscreenIcon />
                </div>
              </div>
            </div>
          )
        ) : (
          <div className="player-desktop player-common">
            <div className="thumbnail">
              {currentArticle && currentArticle.thumbnail_url ? (
                <img src={currentArticle.thumbnail_url} />
              ) : null}
              <div className="meta">
                <div className="title">
                  {currentArticle ? currentArticle.title : ""}
                </div>
                <div className="author">
                  {currentArticle ? (
                    <a
                      href={`http://${currentArticle.article_id}`}
                      target="_blank"
                      className="link"
                    >
                      {getHostName(currentArticle.article_id)}
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="main">
              <div className="buttons-outer">
                <div className="buttons">
                  <div>
                    <img
                      src={Prev}
                      alt="Previous article"
                      onClick={onPreviousArticle}
                    />
                  </div>
                  <div>
                    <img
                      height="30"
                      src={Backward}
                      alt="Rewind 30 seconds"
                      onClick={seekBackward}
                    />
                  </div>
                  {renderPlayButton()}
                  <div>
                    <img
                      height="30"
                      src={Forward}
                      alt="Go forward 30 seconds"
                      onClick={seekForward}
                    />
                  </div>
                  <div>
                    <img
                      src={Next}
                      alt="Next article"
                      onClick={onNextArticle}
                    />
                  </div>
                </div>
                <div className="buttons-other">
                  <a
                    href={userStore.customerPortalUrl}
                    target="_blank"
                    style={{
                      paddingLeft: "5px",
                      display:
                        listeningWhere === "/embed" && isSignedIn
                          ? "display"
                          : "none",
                    }}
                  >
                    <SettingsIcon />
                  </a>
                </div>
                {onDisablePlayer ? (
                  <div className="close-button" onClick={onDisablePlayer}>
                    <CancelIcon /> Remove Player
                  </div>
                ) : null}
              </div>
              <div className="timestamp-progress">
                <div className="timestamp">{formatTime(timestamp)}</div>
                <ProgressBar {...{ timestamp, duration, onSeek }} />
                <div className="duration">{formatTime(duration)}</div>{" "}
              </div>
            </div>
            <div className="speed">
              <div className="title">Speed: {playbackSpeed}x</div>
              <input
                type="range"
                onChange={updatePlaybackSpeed}
                min={playbackStepMin}
                max={playbackStepMax}
                step={playbackStep}
                value={playbackSpeed * 10000}
              />
            </div>
          </div>
        )}
      </>
    );
  }
);

export default Player;

export { default as PlayerWrapper } from './PlayerWrapper';
